import { Delete, Edit, Email as EmailIcon, Group, Person, Phone as PhoneIcon } from '@mui/icons-material';
import { Button, IconButton, Tooltip } from '@mui/material';
import { Chip } from '@jotpsych/ui';

import type { AdminProviderLookupUser } from '@store/generated/admin';

import { CopyIconButton } from './copy-icon-button';
import { RoleChips } from './role-chips';

interface ProviderDetailHeaderProps {
  provider: AdminProviderLookupUser;
  noteCount: number;
  onEditPhone: () => void;
  onDeletePhone: () => void;
  onOpenOrganization: (organizationId: string) => void;
}

const DOSESPOT_STATUS_LABELS: Record<string, string> = {
  completed: 'Connected',
  idp_needed: 'Identity check needed',
  tfa_needed: 'Two-factor needed',
};

function IdentifierRow({
  label,
  value,
  copyLabel,
}: {
  label: string;
  value: string;
  copyLabel: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs uppercase tracking-wide text-gray-400 w-16 shrink-0">{label}</span>
      <span className="text-xs font-mono text-gray-600 break-all">{value}</span>
      <CopyIconButton value={value} label={copyLabel} fontSize={13} />
    </div>
  );
}

export function ProviderDetailHeader({
  provider,
  noteCount,
  onEditPhone,
  onDeletePhone,
  onOpenOrganization,
}: ProviderDetailHeaderProps) {
  const phone = provider.user_info?.mobile_phone_num;
  const email = provider.user_info?.email;
  const dosespotStatus = provider.dosespot_onboarding_status;

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-6 py-5">
        <div className="flex items-start justify-between gap-6">
          {/* Identity */}
          <div className="flex items-start min-w-0">
            <div className="h-16 w-16 shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
              <Person className="h-8 w-8 text-blue-600" />
            </div>

            <div className="ml-6 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900">{provider.name}</h1>
                {provider.membership_status && (
                  <Chip
                    label={provider.membership_status}
                    variant="soft"
                    size="xSmall"
                    color={provider.membership_status === 'active' ? 'success' : 'error'}
                  />
                )}
              </div>

              {/* Contact */}
              <div className="flex flex-col gap-1 mt-2">
                {email && (
                  <div className="flex items-center gap-1">
                    <EmailIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{email}</span>
                    <CopyIconButton value={email} label="Email" />
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <PhoneIcon className="h-4 w-4 text-gray-400" />
                  {phone ? (
                    <>
                      <span className="text-sm text-gray-600">{phone}</span>
                      <CopyIconButton value={phone} label="Phone number" />
                    </>
                  ) : (
                    <span className="text-sm text-gray-400 italic">No phone number</span>
                  )}
                  <Tooltip title={phone ? 'Edit phone number' : 'Add phone number'}>
                    <IconButton
                      size="small"
                      aria-label={phone ? 'Edit phone number' : 'Add phone number'}
                      onClick={onEditPhone}
                    >
                      <Edit sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  {phone && (
                    <Tooltip title="Remove phone number">
                      <IconButton size="small" aria-label="Remove phone number" onClick={onDeletePhone}>
                        <Delete sx={{ fontSize: 14, color: 'error.main' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>

              {/* Organization + the roles held within it */}
              {provider.organization_name && (
                <div className="mt-4">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Group className="h-4 w-4" />}
                    onClick={() => provider.organization_id && onOpenOrganization(provider.organization_id)}
                  >
                    {provider.organization_name}
                  </Button>
                  <div className="flex items-center gap-1 flex-wrap mt-2">
                    <RoleChips roles={provider.roles} emptyLabel="No roles in this organization" />
                  </div>
                </div>
              )}

              {/* Identifiers */}
              <div className="flex flex-col gap-0.5 mt-4">
                <IdentifierRow label="User ID" value={provider.provider_id} copyLabel="User ID" />
                {provider.organization_id && (
                  <IdentifierRow
                    label="Org ID"
                    value={provider.organization_id}
                    copyLabel="Organization ID"
                  />
                )}
              </div>
            </div>
          </div>

          {/* At a glance */}
          <div className="flex flex-col items-end gap-3 shrink-0">
            <div className="text-right">
              <div className="text-sm text-gray-500">Notes</div>
              <div className="text-2xl font-bold text-gray-900">{noteCount}</div>
            </div>
            {dosespotStatus && (
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">DoseSpot</div>
                <Chip
                  label={DOSESPOT_STATUS_LABELS[dosespotStatus] ?? dosespotStatus}
                  variant="soft"
                  size="xSmall"
                  color={dosespotStatus === 'completed' ? 'success' : 'warning'}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
